import { type MouseEvent, useState } from "react";
import { useNavigate } from "react-router";

import { t } from "i18next";

import { Menu as MenuIcon } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar
} from "@mui/material";

import { theme } from "@/src/theme/theme";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";

import LanguageSwitcher from "../../../localizations/languageSwitcher";
import { notificationAtom } from "../../../state/notificationAtom";
import { BaanaLogo } from "../../atoms";
import UserInfo from "./UserInfo";

import { useSetAtom } from "jotai";

const AdminHeader = () => {
  const navigate = useNavigate();
  const setNotification = useSetAtom(notificationAtom);
  const { isAuthenticated, isAuthenticating, user, login, logout } = useAuth();
  const [adminMenuToggleButtonElement, setadminMenuToggleButtonElement] =
    useState<null | HTMLElement>(null);

  const open = Boolean(adminMenuToggleButtonElement);

  const openUserMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setadminMenuToggleButtonElement(event.currentTarget);
  };

  const closeUserMenu = () => {
    setadminMenuToggleButtonElement(null);
  };

  const handleNavigation = (url: string) => {
    navigate(url);
  };

  return (
    <>
      <AppBar position="sticky" id="adminHeader">
        <Toolbar
          sx={{
            justifyContent: "space-between"
          }}
        >
          <Box
            id="adminLogoButton"
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              pt: 1
            }}
            onClick={() => {
              handleNavigation("/admin");
            }}
          >
            <Box sx={{ width: "6rem" }}>
              <BaanaLogo color={theme.palette.semantic.primary.contrastText} />
            </Box>
          </Box>

          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            {isAuthenticated && !isAuthenticating && (
              <UserInfo
                firstName={user?.firstName}
                lastName={user?.lastName}
                roleName="Pääkäyttäjä"
              />
            )}

            {!isAuthenticated && (
              <Button
                id="adminMenuLoginButton"
                variant="text"
                sx={{
                  color: theme.palette.semantic.primary.contrastText,
                  marginRight: 1
                }}
                onClick={() => {
                  login("admin", LoginSource.ADMIN_HEADER);
                }}
              >
                {t("general.loginShort")}
              </Button>
            )}
            <LanguageSwitcher />

            <IconButton
              id="adminMenuToggleButton"
              size="large"
              edge="start"
              color="inherit"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={openUserMenu}
              sx={{ borderRadius: 2 }}
            >
              <MenuIcon />
            </IconButton>

            {!isAuthenticating && (
              <Menu
                id="adminMenu"
                anchorEl={adminMenuToggleButtonElement}
                open={open}
                onClose={() => closeUserMenu()}
                sx={{ ".MuiPaper-root": { borderRadius: "6px" } }}
                slotProps={{
                  list: {
                    "aria-labelledby": "adminMenuToggleButton"
                  }
                }}
              >
                {isAuthenticated && (
                  <MenuItem
                    id="adminMenuLogoutMenuItem"
                    onClick={() => {
                      closeUserMenu();
                      setNotification(null);
                      logout();
                    }}
                  >
                    {t("general.logout")}
                  </MenuItem>
                )}

                {!isAuthenticated && (
                  <MenuItem
                    id="adminMenuLoginMenuItem"
                    onClick={() => {
                      closeUserMenu();
                      setNotification(null);
                      login("admin", LoginSource.ADMIN_MENU);
                    }}
                  >
                    {t("general.login")}
                  </MenuItem>
                )}
              </Menu>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default AdminHeader;
